import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useNavigate, useLocation } from "react-router-dom";

import {
  userRegister,
  userRegisterAgencyAdmin,
  userCreateAgencyUser,
} from "@/store/auth";

import { Agency } from "@/types"

import AppButton from "@/components/AppButton";
import AppLink from "@/components/AppLink";
import AppCard from "@/components/AppCard";
import AppBox from "@/components/AppBox";
import AppTooltip from "@/components/AppTooltip";
import AppInput from "@/components/AppInput";
import AppText from "@/components/AppText";
import AppIcon from "@/components/AppIcon";
import AppDropdown from "@/components/AppDropdown";

interface RegisterProps {
  agency?: Agency;
  onSubmit?: () => void;
  isAgencyForm?: boolean;
}

const sanitizeAgencyName = (name: string) => {
  return name
      .toLowerCase()                // Convert to lowercase
      .replace(/\s+/g, '')          // Remove all spaces
      .replace(/[^a-z0-9]/g, '');   // Remove unsupported characters (only letters and numbers allowed)
}

const generateRandomString = (length: number = 6) => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const generateFakeEmail = (agency?: Agency) => {
  return agency?.id
    ? `${sanitizeAgencyName(agency.name)}+${generateRandomString(4)}@lsarespond.com`
    : '';
};

const timezones = [
  { label: "Eastern", value: "America/New_York" },
  { label: "Central", value: "America/Chicago" },
  { label: "Mountain", value: "America/Denver" },
  { label: "Pacific", value: "America/Los_Angeles" },
  { label: "Outside US", value: "outside_us" },
];

const Register: React.FC<RegisterProps> = ({
  agency = null,
  onSubmit = null,
  isAgencyForm = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { status } = useAppSelector((state) => state.auth);

  const [form, setForm] = useState({
    name: "",
    email: agency?.id ? generateFakeEmail(agency) : '',
    password: "",
    customer_id: isAgencyForm ? 'agency' : "",
    company: "",
    timezone: timezones[0].value,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [integration, setIntegration] = useState<{ id: number; name: string; pro?: boolean }>({
    id: 0,
    name: "",
    pro: false,
  });

  // Set a default password if agency exists
  useEffect(() => {
    if (agency?.name) {
      const agencyPassword = agency.name.slice(0, 2).toUpperCase() + "Z9lcNi";
      setForm((prev) => ({ ...prev, password: agencyPassword }));
    }
  }, [agency]);

  // Detect integration based on URL
  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];
    
    if (lastSegment === "sf" || lastSegment === "sfpro") {
      setIntegration({
        id: 1,
        name: getIntegrationName(1),
        pro: lastSegment === "sfpro",
      });
    }
    
    if (lastSegment === "workiz") {
      setIntegration({
        id: 2,
        name: getIntegrationName(2),
      });
    }
    
    if (lastSegment === "jobber") {
      setIntegration({
        id: 3,
        name: getIntegrationName(3),
      });
    }
  }, [location.pathname]);

  const getIntegrationName = (id: number) => {
    switch (id) {
      case 1:
        return "Service Fusion";
      case 2:
        return "Workiz";
      case 3:
        return "Jobber";
      default:
        return "Unknown Integration";
    }
  };

  const handleSubmit = async () => {
    Object.keys(form).forEach((field) => {
      if (!form[field as keyof typeof form] && field !== "company_id" && field !== "timezone") {
        setErrors((prev) => ({
          ...prev,
          [field]: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`,
        }));
      }
    });

    if (agency) {
      const data = {
        company: form.company,
        name: form.company,
        email: form.email,
        password: form.password,
        timezone: form.timezone,
        customer_id: form.customer_id.replace(/-/g, ""),
        agency_id: agency.id,
      };
      const res = await dispatch(userCreateAgencyUser(data)).unwrap();
      if (!res.error) {
        onSubmit?.();
      }
    } else {
      const payload = integration.id
        ? { ...form, integration }
        : form;

      if (isAgencyForm) {
        const res = await dispatch(
          userRegisterAgencyAdmin({
            ...payload,
            link: `${window.location.origin}/activate/{hash}`,
          })
        ).unwrap();
        if (!res.error) navigate("/agency");
      } else {
        const res = await dispatch(
          userRegister({
            ...payload,
            link: `${window.location.origin}/activate/{hash}`,
            customer_id: payload.customer_id.replace(/-/g, ""),
          })
        ).unwrap();
        if (!res.error) navigate("/");
      }
    }
  };

  const handleChange =
    (field: keyof typeof form) =>
    (value: string): void => {
      if (isAgencyForm && field === 'name') {
        setForm((prev) => ({ ...prev, customer_id: value.toLocaleLowerCase() }));
      }
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  return (
    <AppCard className={agency ? "border-0 shadow-none" : ""}>
      <AppBox column gap={4}>
        {
          !agency
            ? <AppInput
                type="text"
                label={agency ? "Client Name" : "Your Name"}
                fullWidth
                onChange={handleChange("name")}
                value={form.name}
                error={errors.name}
                onPressEnter={handleSubmit}
              />
            : null
        }

        <AppInput
          type="text"
          label={(isAgencyForm ? 'Agency' : 'Company') + " Name"}
          fullWidth
          onChange={handleChange('company')}
          value={form.company}
          error={errors.company}
          onPressEnter={handleSubmit}
        />

        {!isAgencyForm
          ? <AppInput
              type="text"
              label={
                <AppBox align="center" gap={1}>
                  <AppText color="dark" variant="label">
                    LSA Customer ID
                  </AppText>
    
                  <AppTooltip title="Please log into your Google LSA account and enter your Customer ID number.">
                    <AppIcon name="info" size="sm" />
                  </AppTooltip>
                </AppBox>
              }
              fullWidth
              onChange={handleChange("customer_id")}
              value={form.customer_id}
              error={errors.customer_id}
              onPressEnter={handleSubmit}
            />
          : null
        }

        {/* Timezone selector */}
        <AppBox column gap={1}>
          <AppText variant="label">Timezone</AppText>
          <AppDropdown
            label={timezones.find((tz) => tz.value === form.timezone)?.label || "Timezone"}
            items={timezones}
            value={form.timezone}
            onChange={(v) => {
              const val = String(v);
              if (errors.timezone) setErrors((prev) => ({ ...prev, timezone: "" }));
              setForm((prev) => ({ ...prev, timezone: val }));
            }}
          />
        </AppBox>

        {!agency
          ? <AppInput
              type="text"
              label="Email Address"
              fullWidth
              name="register-email"
              onChange={handleChange("email")}
              value={form.email}
              error={errors.email}
              onPressEnter={handleSubmit}
            />
          : null
        }

        {!agency
          ? <AppInput
              type={agency ? "text" : "password"}
              label="Create a Password"
              name="register-password"
              fullWidth
              onChange={handleChange("password")}
              value={form.password}
              error={errors.password}
              disabled={!!agency}
              onPressEnter={handleSubmit}
            />
          : null
        }

        {/* {integration.id ? getIntegrationComponent() : null} */}

        <AppButton onClick={handleSubmit} isLoading={status === "loading"}>
          {agency ? "Add User" : "Sign Up"}
        </AppButton>

        {!agency && (
          <AppBox>
            <AppLink to="/">Log In</AppLink>
          </AppBox>
        )}
      </AppBox>
    </AppCard>
  );
};

export default Register;